import {useState} from 'react';

import {isNil, isNotEmpty, isNotNil} from 'ramda';

import {useLazyGetMultipleAuditDataQuery} from '@dms/api/core';
import {useLazyListVehicleAlbumsQuery} from '@dms/api/sales';
import {useLazyGetServiceCheckInQuery, useLazyGetServiceHandOverQuery} from '@dms/api/workshop';

import {Nullish} from 'shared/web';

import {getGridPhotosFromAudit, getGridVideosFromAudit} from '../../../utils/getGridPhotos';
import {HistoricalServiceCaseAlbum} from '../types/HistoricalServiceCaseAlbum';
import {HistoricalServiceCaseAlbumGroup} from '../types/HistoricalServiceCaseAlbumGroup';
import {ServiceCaseAuditData, isServiceCaseAuditDataEmpty} from '../types/ServiceCaseAuditData';
import {buildServiceCaseAlbumGroups} from '../utils/buildServiceCaseAlbumGroups';

interface UseHistoricalServiceCaseAlbumsParams {
  vehicleId: string;
  ignoredServiceCaseId?: string | Nullish;
}

const hasAlbumPhotos = (albums: HistoricalServiceCaseAlbum[]): boolean =>
  albums.some((album) => (album.photoCount ?? 0) > 0);

export function useHistoricalServiceCaseAlbums(params: UseHistoricalServiceCaseAlbumsParams) {
  const [listVehicleAlbums] = useLazyListVehicleAlbumsQuery();
  const [getServiceCheckIn] = useLazyGetServiceCheckInQuery();
  const [getServiceHandOver] = useLazyGetServiceHandOverQuery();
  const [getMultipleAuditData] = useLazyGetMultipleAuditDataQuery();

  const [albumGroups, setAlbumGroups] = useState<HistoricalServiceCaseAlbumGroup[] | Nullish>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const loadGroupAuditData = async (serviceCaseId: string): Promise<ServiceCaseAuditData> => {
    const [checkin, handover] = await Promise.all([
      getServiceCheckIn({serviceCaseId})
        .unwrap()
        .catch(() => null),
      getServiceHandOver({serviceCaseId})
        .unwrap()
        .catch(() => null),
    ]);

    const checkinInspectionIds = checkin?.inspectionId ?? [];
    const handoverInspectionIds = handover?.inspectionId ?? [];

    const [checkinAudit, handoverAudit] = await Promise.all([
      isNotEmpty(checkinInspectionIds)
        ? getMultipleAuditData({auditIds: checkinInspectionIds})
            .unwrap()
            .catch(() => [])
        : Promise.resolve([]),
      isNotEmpty(handoverInspectionIds)
        ? getMultipleAuditData({auditIds: handoverInspectionIds})
            .unwrap()
            .catch(() => [])
        : Promise.resolve([]),
    ]);

    return {
      checkinPhotos: getGridPhotosFromAudit(checkinAudit.flatMap((audit) => audit.categories)),
      checkinVideos: getGridVideosFromAudit(checkinAudit.flatMap((audit) => audit.categories)),
      handoverPhotos: getGridPhotosFromAudit(handoverAudit.flatMap((audit) => audit.categories)),
      handoverVideos: getGridVideosFromAudit(handoverAudit.flatMap((audit) => audit.categories)),
      checkinAuditId: checkinAudit[0]?.id,
      handoverAuditId: handoverAudit[0]?.id,
    };
  };

  const load = async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      const vehicleAlbums = await listVehicleAlbums({vehicleId: params.vehicleId})
        .unwrap()
        .catch(() => null);

      if (isNil(vehicleAlbums)) {
        setIsError(true);
        return;
      }

      const groups = buildServiceCaseAlbumGroups(vehicleAlbums, params.ignoredServiceCaseId);

      const resolvedGroups = await Promise.all(
        groups.map(async (albums) => {
          const serviceCaseId = albums[0]?.serviceCaseId;

          if (isNil(serviceCaseId)) {
            return null;
          }

          const auditData = await loadGroupAuditData(serviceCaseId);

          if (!hasAlbumPhotos(albums) && isServiceCaseAuditDataEmpty(auditData)) {
            return null;
          }

          return {serviceCaseId, albums, auditData};
        })
      );

      setAlbumGroups(resolvedGroups.filter(isNotNil));
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return {albumGroups, isLoading, isError, load};
}
